import React, { useCallback } from 'react';
import { Control, UseFormWatch, FieldPath } from 'react-hook-form';
import { discounts, DiscountTypes } from 'api';
import { FormInput } from 'components';
import { AutocompleteSearchItem } from 'api/types/common';

interface Props {
  control: Control<DiscountTypes.ItemCreateParams> | undefined;
  name: FieldPath<DiscountTypes.ItemCreateParams> | string;
  watch: UseFormWatch<DiscountTypes.ItemCreateParams>;
  initialValue: AutocompleteSearchItem | undefined;
  onChange?: (
    value: DiscountTypes.CartValueTier['free_gift_collection'],
  ) => void;
}

const FreeGiftCollectionsInput = (props: Props): JSX.Element | null => {
  const { control, watch, name, onChange, initialValue } = props;

  const brand = watch('brand');
  const countryTeam = watch('country_team');
  const shopId = watch('shop_id');

  const handleAutocompleteChange = useCallback(
    (
      value: AutocompleteSearchItem | AutocompleteSearchItem[] | null,
      onFieldChange: (value: string | string[] | null) => void,
    ) => {
      if (value === null) {
        onFieldChange('');
        onChange?.(undefined);
        return;
      }

      if (value && !Array.isArray(value)) {
        const newValue =
          'originData' in value
            ? value.originData
            : {
                id: value._id.$oid,
                title: value.name,
                image: value.image,
              };

        onFieldChange(value._id.$oid);
        if (onChange) {
          onChange(newValue);
        }
      }
    },
    [onChange],
  );

  const getCollections = useCallback(
    (query: string) =>
      discounts
        .autocompleteCollections({
          collection_title: query,
          brand,
          shop_id: shopId,
          country_team: countryTeam,
        })
        .then((res) =>
          res.data.collections.edges.map((e) => ({
            name: e.node.title,
            _id: { $oid: e.node.id },
            image: e.node.image?.url || '',
            originData: {
              id: e.node.id,
              title: e.node.title,
              image: e.node.image ? e.node.image.url : '',
            },
          })),
        ),
    [brand, countryTeam, shopId],
  );

  return (
    <FormInput
      control={control}
      name={name}
      inputType="autocompleteSearch"
      label="Free Gift Collection"
      disabled={!shopId}
      initialValue={initialValue}
      onChange={handleAutocompleteChange}
      queryKey={discounts.endpoints.autocompleteCollections}
      queryFn={getCollections}
    />
  );
};

export default FreeGiftCollectionsInput;
